import {
  Alert,
  Box,
  Button,
  Center,
  Grid,
  Group,
  Loader,
  Progress,
  rem,
  Stack,
  Text,
} from '@mantine/core';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconArrowRight,
  IconDatabaseImport,
  IconEdit,
  IconExternalLink,
  IconTrash,
} from '@tabler/icons-react';
import { pageWrapper } from '@/Styles';
import { clientRoutes } from '@/ApiPaths';
import { PageHeader } from '@/components/PageHeader';
import { DropModelZone } from '@/components/DropModelZone';
import { ProductForm } from '@/components/ProductForm/ProductForm';
import { ModelViewer } from '@/components/ModelViewer/ModelViewer';
import { PreviewImageManager } from '@/components/PreviewImageManager/PreviewImageManager';
import { AnnotationEditor } from '@/components/Annotations';
import { ProductListingManager } from '@/components/ProductListingManager';
import { FloatingProductSubmitButton } from '@/components/FloatingProductSubmitButton';
import { SuccessPopup } from '@/components/SuccessPopup';
import { ProductBatchStepper } from '@/components/ProductEditor/ProductBatchStepper';
import { ProductBatchSuccessModal } from '@/components/ProductEditor/ProductBatchSuccessModal';
import {
  PRODUCT_EDITOR_FORM_ID,
  useProductEditor,
  type ProductEditorVariant,
} from '@/hooks/useProductEditor';
import {
  useProductBatchCreate,
  draftToFormValues,
  draftPreviewUri,
} from '@/hooks/useProductBatchCreate';
import { isTypingKeyboardTarget } from '@/utils/keyboardShortcuts';
import { resetModelViewerCamera } from '@/utils/modelViewerZoom';

type ProductEditorProps = {
  variant: ProductEditorVariant;
};

/**
 * Shared Create / Edit product workspace.
 * Route pages only choose `variant`; all layout and behavior live here.
 */
export function ProductEditor({ variant }: ProductEditorProps) {
  const editor = useProductEditor(variant);
  const {
    t,
    session,
    color,
    id,
    isEditingExisting,
    canEditProduct,
    editBlockReason,
    isLoadingEdit,
    isRestoringFromTrash,
    handleRestoreFromTrash,
    showWorkspace,
    modelViewerRef,
    annotationEditorRef,
    modelFile,
    modelUrl,
    nonGltfModel,
    preparedGlb,
    setModelFile,
    applyPreparedModel,
    liveFormValues,
    modelPreviewUri,
    setModelPreviewUri,
    initialCameraOrbit,
    setInitialCameraOrbit,
    initialCameraTarget,
    setInitialCameraTarget,
    initialCameraFieldOfView,
    setInitialCameraFieldOfView,
    showModelUploader,
    setShowModelUploader,
    modelReplacement,
    setModelReplacement,
    annotations,
    setAnnotations,
    isAnnotationMode,
    setIsAnnotationMode,
    presentationSettings,
    setPresentationSettings,
    availableAnimations,
    productData,
    productFormInitialValues,
    suggestedTags,
    previousTags,
    modelUploadProductName,
    isSubmitting,
    hasPendingChanges,
    successPopupData,
    setSuccessPopupData,
    handleFormSubmit,
    handleFormValuesChange,
    exportNonGltfModel,
    cancelModelReplacement,
    handleResetForm,
  } = editor;

  const [helpOpened, setHelpOpened] = useState(false);

  const batch = useProductBatchCreate({
    enabled: variant === 'create' && !isEditingExisting,
    modelViewerRef,
    applyPreparedModel,
    onPreviewUriChange: setModelPreviewUri,
    onCameraChange: (orbit, target, fov) => {
      setInitialCameraOrbit(orbit);
      setInitialCameraTarget(target);
      setInitialCameraFieldOfView(fov);
    },
    onRestoreAnnotations: (next) => {
      setAnnotations(next);
      setIsAnnotationMode(false);
    },
  });

  const handleCreateDrop = useCallback(
    (files: File[]) => {
      if (!files?.length) return;

      const alreadyBatch = batch.isBatchMode;
      const singleLoaded = Boolean(modelFile);

      if (!alreadyBatch && !singleLoaded && files.length === 1) {
        setModelFile(files[0]);
        return;
      }

      if (!alreadyBatch && singleLoaded && modelFile) {
        batch.addFiles(files, {
          file: modelFile,
          productName: liveFormValues?.productName || modelUploadProductName,
          articleNumber: liveFormValues?.articleNumber,
          productPage: liveFormValues?.productPage,
          descriptionHtml: liveFormValues?.descriptionHtml,
          isPublic: liveFormValues?.isPublic,
          category: liveFormValues?.category,
          metadata: liveFormValues?.metadata,
          linkShareMode: liveFormValues?.linkShareMode,
          linkSharePassword: liveFormValues?.linkSharePassword,
          modelUrl,
          previewUri: modelPreviewUri,
          nonGltfModel,
          cameraOrbit: initialCameraOrbit,
          cameraTarget: initialCameraTarget,
          cameraFov: initialCameraFieldOfView,
          preparedGlb,
          annotations,
        });
        return;
      }

      batch.addFiles(files);
    },
    [
      batch,
      initialCameraFieldOfView,
      initialCameraOrbit,
      initialCameraTarget,
      liveFormValues,
      modelFile,
      modelPreviewUri,
      modelUploadProductName,
      modelUrl,
      nonGltfModel,
      preparedGlb,
      annotations,
      setModelFile,
    ]
  );

  const pendingDraftCount = batch.pendingDraftCount;

  useEffect(() => {
    if (!showWorkspace) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const typing = isTypingKeyboardTarget(event.target);
      const key = event.key.toLowerCase();
      const mod = event.metaKey || event.ctrlKey;

      if (key === 'escape') {
        if (helpOpened) {
          event.preventDefault();
          setHelpOpened(false);
          return;
        }
        if (isAnnotationMode) {
          event.preventDefault();
          setIsAnnotationMode(false);
        }
        return;
      }

      if (mod && key === 's') {
        event.preventDefault();
        if (batch.isBatchMode) {
          if (batch.isSubmitting) return;
          if (batch.isLastDraft) {
            void batch.submitAll();
          } else {
            batch.goNext();
          }
          return;
        }
        if (!modelUrl || isSubmitting || !hasPendingChanges) return;
        const form = document.getElementById(PRODUCT_EDITOR_FORM_ID);
        if (form instanceof HTMLFormElement) {
          form.requestSubmit();
        }
        return;
      }

      if (typing || mod || event.altKey) return;

      if (key === '?' || key === 'h') {
        if (!modelUrl) return;
        event.preventDefault();
        setHelpOpened(true);
        return;
      }

      if (key === 'a') {
        if (!modelUrl || batch.isSubmitting) return;
        event.preventDefault();
        setIsAnnotationMode((prev) => !prev);
        return;
      }

      if (key === 'r') {
        if (!modelUrl) return;
        event.preventDefault();
        const mv = modelViewerRef.current;
        if (!mv) return;
        resetModelViewerCamera(mv, {
          cameraOrbit: initialCameraOrbit,
          cameraTarget: initialCameraTarget,
          cameraFieldOfView: initialCameraFieldOfView,
        });
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    showWorkspace,
    helpOpened,
    isAnnotationMode,
    modelUrl,
    isSubmitting,
    hasPendingChanges,
    setIsAnnotationMode,
    modelViewerRef,
    initialCameraOrbit,
    initialCameraTarget,
    initialCameraFieldOfView,
    batch.isLastDraft,
    batch.goNext,
    batch.isBatchMode,
    batch.isSubmitting,
    batch.submitAll,
  ]);

  const createDropzone = (
    <DropModelZone
      corporateColor={color}
      multiple
      compact={batch.isBatchMode || Boolean(modelFile)}
      disabled={batch.isSubmitting}
      onDrop={handleCreateDrop}
      onReject={() => {
        if (!batch.isBatchMode && !modelFile) setModelFile(null);
      }}
    />
  );

  return (
    <SessionAuth>
      <Center style={{ ...pageWrapper, justifyContent: 'flex-start', paddingTop: rem(20) }}>
        <Box mx="auto" style={{ width: '80%', paddingBottom: rem(88) }}>
          <PageHeader
            icon={
              isEditingExisting ? (
                <IconEdit size={22} stroke={1.75} />
              ) : (
                <IconDatabaseImport size={22} stroke={1.75} />
              )
            }
            title={
              isEditingExisting
                ? t('editProduct.editTitle')
                : batch.isBatchMode
                  ? t('editProduct.batch.createTitle')
                  : t('editProduct.createTitle')
            }
            subtitle={
              isEditingExisting && canEditProduct && !showModelUploader
                ? t('editProduct.updateDescription')
                : !isEditingExisting
                  ? t('editProduct.batch.pageSubtitle')
                  : undefined
            }
            accentColor={color}
            mb="md"
            actions={
              isEditingExisting && canEditProduct ? (
                <Button
                  component="a"
                  href={clientRoutes.product.replace(':id', id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="light"
                  color={color}
                  leftSection={<IconExternalLink size={16} />}
                >
                  {t('editProduct.openProductPage')}
                </Button>
              ) : undefined
            }
          />

          {isEditingExisting && isLoadingEdit && !showWorkspace && !editBlockReason && (
            <Center py="xl">
              <Loader color={color} />
            </Center>
          )}

          {isEditingExisting && editBlockReason && (
            <Alert
              color={editBlockReason === 'inTrash' ? 'orange' : 'red'}
              variant="light"
              radius="md"
              icon={
                editBlockReason === 'inTrash' ? (
                  <IconTrash size={20} />
                ) : (
                  <IconAlertCircle size={20} />
                )
              }
              title={
                editBlockReason === 'inTrash'
                  ? t('editProduct.inTrashTitle')
                  : editBlockReason === 'notFound'
                    ? t('editProduct.notFoundTitle')
                    : t('editProduct.loadErrorTitle')
              }
            >
              <Stack gap="md">
                <Text size="sm">
                  {editBlockReason === 'inTrash'
                    ? t('editProduct.inTrashMessage')
                    : editBlockReason === 'notFound'
                      ? t('editProduct.notFoundMessage')
                      : t('editProduct.loadErrorMessage')}
                </Text>
                <Group gap="sm">
                  {editBlockReason === 'inTrash' && (
                    <>
                      <Button
                        color={color}
                        loading={isRestoringFromTrash}
                        onClick={() => void handleRestoreFromTrash()}
                      >
                        {t('editProduct.inTrashRestore')}
                      </Button>
                      <Button
                        component={Link}
                        to={`${clientRoutes.dashboard}#trash`}
                        variant="default"
                      >
                        {t('editProduct.inTrashOpenTrash')}
                      </Button>
                    </>
                  )}
                  {(editBlockReason === 'notFound' || editBlockReason === 'error') && (
                    <Button component={Link} to={clientRoutes.dashboard} variant="default">
                      {t('editProduct.backToDashboard')}
                    </Button>
                  )}
                </Group>
              </Stack>
            </Alert>
          )}

          {showWorkspace && (
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2vw',
                  }}
                >
                  {!isEditingExisting && createDropzone}

                  {isEditingExisting && showModelUploader && (
                    <div
                      style={{
                        width: '100%',
                        minHeight: '200px',
                        backgroundColor: 'var(--mantine-color-body)',
                        borderRadius: '30px',
                        padding: '20px',
                        border: '1px solid var(--mantine-color-default-border)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '10px',
                        }}
                      >
                        <Text fw={500}>{t('editProduct.uploadNewModel')}</Text>
                        <Button
                          onClick={cancelModelReplacement}
                          color="red"
                          variant="light"
                          size="xs"
                        >
                          {t('editProduct.editCancel')}
                        </Button>
                      </div>
                      <DropModelZone
                        corporateColor={color}
                        onDrop={(files: File[]) => {
                          const newFile = !files || files.length === 0 ? null : files[0];
                          setModelFile(newFile);
                          setModelReplacement(newFile !== null);
                        }}
                        onReject={() => {
                          setModelFile(null);
                          setModelReplacement(false);
                        }}
                      />
                      {modelFile && (
                        <Text size="sm" fw={500} mt="xs" c="green">
                          {t('editProduct.newModel')} {modelFile.name}
                        </Text>
                      )}
                    </div>
                  )}

                  {!session.loading && session.doesSessionExist && (
                    <div
                      style={{
                        width: '100%',
                        minHeight: '200px',
                        backgroundColor: 'var(--mantine-color-body)',
                        borderRadius: '30px',
                        padding: '20px',
                        border: '1px solid var(--mantine-color-default-border)',
                      }}
                    >
                      {batch.isBatchMode ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                          {batch.remainingSlots != null ? (
                            <Text size="xs" c="dimmed">
                              {t('editProduct.batch.slotsHint', {
                                used: batch.usedSlots ?? 0,
                                total: batch.productSlots,
                                remaining: batch.remainingSlots,
                              })}
                            </Text>
                          ) : null}

                          <ProductBatchStepper
                            drafts={batch.drafts}
                            activeDraftId={batch.activeDraftId}
                            accentColor={color}
                            disabled={batch.isSubmitting}
                            onSelect={batch.selectDraft}
                            onRemove={batch.removeDraft}
                          />

                          <PreviewImageManager
                            captureOwnerId={batch.activeDraft?.localId}
                            modelPreviewUri={
                              batch.activeDraft ? draftPreviewUri(batch.activeDraft) : ''
                            }
                            modelViewerRef={modelViewerRef}
                            onPreviewUriChange={(uri) => {
                              const ownerId = batch.activeDraft?.localId;
                              const ownerModelUrl = batch.activeDraft?.modelUrl ?? '';
                              setModelPreviewUri(uri);
                              if (ownerId) {
                                batch.commitOwnedPatch(ownerId, ownerModelUrl, {
                                  previewUri: uri,
                                });
                              }
                            }}
                            onInitialCameraOrbitChange={(orbit) => {
                              const ownerId = batch.activeDraft?.localId;
                              const ownerModelUrl = batch.activeDraft?.modelUrl ?? '';
                              setInitialCameraOrbit(orbit);
                              if (ownerId) {
                                batch.commitOwnedPatch(ownerId, ownerModelUrl, {
                                  cameraOrbit: orbit,
                                });
                              }
                            }}
                            onInitialCameraTargetChange={(target) => {
                              const ownerId = batch.activeDraft?.localId;
                              const ownerModelUrl = batch.activeDraft?.modelUrl ?? '';
                              setInitialCameraTarget(target);
                              if (ownerId) {
                                batch.commitOwnedPatch(ownerId, ownerModelUrl, {
                                  cameraTarget: target,
                                });
                              }
                            }}
                            onInitialCameraFieldOfViewChange={(fov) => {
                              const ownerId = batch.activeDraft?.localId;
                              const ownerModelUrl = batch.activeDraft?.modelUrl ?? '';
                              setInitialCameraFieldOfView(fov);
                              if (ownerId) {
                                batch.commitOwnedPatch(ownerId, ownerModelUrl, {
                                  cameraFov: fov,
                                });
                              }
                            }}
                            modelUrl={batch.activeDraft?.modelUrl || ''}
                            autoCaptureFromViewerOnLoad
                          />

                          {batch.activeDraft?.modelUrl ? (
                            <Box>
                              <AnnotationEditor
                                key={batch.activeDraft.localId}
                                ref={annotationEditorRef}
                                annotations={batch.activeDraft.annotations}
                                onAnnotationsChange={(next) => {
                                  setAnnotations(next);
                                  batch.patchActiveDraft({ annotations: next });
                                }}
                                isAnnotationMode={isAnnotationMode && !batch.isSubmitting}
                                onAnnotationModeChange={(enabled) => {
                                  if (batch.isSubmitting) return;
                                  setIsAnnotationMode(enabled);
                                }}
                                availableAnimations={availableAnimations}
                              />
                            </Box>
                          ) : null}

                          {batch.activeDraft ? (
                            <ProductForm
                              key={batch.activeDraft.localId}
                              hideSubmitButton
                              onSubmit={() => {
                                if (batch.isLastDraft) {
                                  void batch.submitAll();
                                } else {
                                  batch.goNext();
                                }
                              }}
                              isSubmitting={batch.isSubmitting}
                              isDisabled={batch.isSubmitting}
                              initialValues={draftToFormValues(batch.activeDraft)}
                              suggestedTags={suggestedTags}
                              previousTags={previousTags}
                              onValuesChange={(values) =>
                                batch.syncFormValues(batch.activeDraft!.localId, values)
                              }
                            />
                          ) : null}

                          <Group justify="space-between">
                            <Button
                              variant="default"
                              leftSection={<IconArrowLeft size={16} />}
                              disabled={batch.activeIndex <= 0 || batch.isSubmitting}
                              onClick={batch.goPrev}
                            >
                              {t('editProduct.batch.backProduct')}
                            </Button>
                            {!batch.isLastDraft ? (
                              <Button
                                color={color}
                                rightSection={<IconArrowRight size={16} />}
                                disabled={batch.isSubmitting}
                                onClick={batch.goNext}
                              >
                                {t('editProduct.batch.nextProduct')}
                              </Button>
                            ) : (
                              <Text size="sm" c="dimmed">
                                {t('editProduct.batch.lastStepHint', {
                                  count: pendingDraftCount,
                                })}
                              </Text>
                            )}
                          </Group>

                          {batch.submitProgress ? (
                            <Box>
                              <Group justify="space-between" align="center" mb={6}>
                                <Text size="sm">
                                  {t('editProduct.batch.progress', {
                                    current: batch.submitProgress.current,
                                    total: batch.submitProgress.total,
                                  })}
                                </Text>
                                <Button
                                  size="xs"
                                  variant="subtle"
                                  color="gray"
                                  onClick={batch.cancelSubmit}
                                >
                                  {t('editProduct.batch.cancelSubmit')}
                                </Button>
                              </Group>
                              <Progress
                                value={
                                  (batch.submitProgress.current / batch.submitProgress.total) * 100
                                }
                                color={color}
                                animated
                              />
                            </Box>
                          ) : null}
                        </div>
                      ) : (
                        <>
                          <PreviewImageManager
                            modelPreviewUri={modelPreviewUri}
                            modelViewerRef={modelViewerRef}
                            onPreviewUriChange={setModelPreviewUri}
                            onInitialCameraOrbitChange={setInitialCameraOrbit}
                            onInitialCameraTargetChange={setInitialCameraTarget}
                            onInitialCameraFieldOfViewChange={setInitialCameraFieldOfView}
                            modelUrl={modelUrl}
                            autoCaptureFromViewerOnLoad={!isEditingExisting}
                          />

                          {modelUrl && (
                            <Box mt="md">
                              <AnnotationEditor
                                ref={annotationEditorRef}
                                annotations={annotations}
                                onAnnotationsChange={setAnnotations}
                                isAnnotationMode={isAnnotationMode}
                                onAnnotationModeChange={setIsAnnotationMode}
                                availableAnimations={availableAnimations}
                              />
                            </Box>
                          )}

                          <ProductForm
                            formId={PRODUCT_EDITOR_FORM_ID}
                            hideSubmitButton
                            onSubmit={handleFormSubmit}
                            isSubmitting={isSubmitting}
                            isDisabled={!modelUrl}
                            isEditing={isEditingExisting}
                            initialValues={
                              isEditingExisting || productData.productName
                                ? productFormInitialValues
                                : undefined
                            }
                            initialLinkSharePasswordSet={Boolean(productData.linkSharePasswordSet)}
                            suggestedTags={suggestedTags}
                            previousTags={previousTags}
                            modelUploadProductName={modelUploadProductName}
                            onValuesChange={handleFormValuesChange}
                          />
                        </>
                      )}
                    </div>
                  )}
                </div>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <div
                  style={{
                    width: '100%',
                    minHeight: '785px',
                    backgroundColor: 'var(--mantine-color-body)',
                    borderRadius: '30px',
                    padding: '20px',
                    border: '1px solid var(--mantine-color-default-border)',
                  }}
                >
                  <ModelViewer
                    modelUrl={modelUrl}
                    modelFile={modelFile}
                    showModelUploader={showModelUploader}
                    modelReplacement={modelReplacement}
                    initialCameraOrbit={initialCameraOrbit}
                    initialCameraTarget={initialCameraTarget}
                    initialCameraFieldOfView={initialCameraFieldOfView}
                    modelViewerRef={modelViewerRef}
                    onModelFileChange={setModelFile}
                    onShowModelUploaderChange={setShowModelUploader}
                    onModelReplacementChange={setModelReplacement}
                    onExportModel={exportNonGltfModel}
                    isEditing={isEditingExisting}
                    annotations={
                      batch.isBatchMode ? (batch.activeDraft?.annotations ?? []) : annotations
                    }
                    isAnnotationMode={isAnnotationMode && !batch.isSubmitting}
                    onAnnotationAdd={(position, normal, orbit, target, cameraFieldOfView) => {
                      annotationEditorRef.current?.handleAnnotationAdd(
                        position,
                        normal,
                        orbit,
                        target,
                        cameraFieldOfView
                      );
                    }}
                    presentationSettings={presentationSettings}
                    onPresentationChange={setPresentationSettings}
                    helpOpened={helpOpened}
                    onHelpOpenedChange={setHelpOpened}
                  />
                </div>
              </Grid.Col>
            </Grid>
          )}

          {!session.loading && session.doesSessionExist && id && canEditProduct && (
            <Box mt="xl">
              <ProductListingManager id={id} />
            </Box>
          )}
        </Box>

        {!session.loading && session.doesSessionExist && showWorkspace && (
          <FloatingProductSubmitButton
            formId={batch.isBatchMode ? undefined : PRODUCT_EDITOR_FORM_ID}
            onClick={
              batch.isBatchMode
                ? () => {
                    if (batch.isLastDraft) {
                      void batch.submitAll();
                    } else {
                      batch.goNext();
                    }
                  }
                : undefined
            }
            label={
              isEditingExisting
                ? t('editProduct.updateReady')
                : batch.isBatchMode
                  ? batch.isLastDraft
                    ? t('editProduct.batch.createAll', { count: Math.max(pendingDraftCount, 1) })
                    : t('editProduct.batch.nextProduct')
                  : t('editProduct.createReady')
            }
            idleLabel={
              isEditingExisting
                ? t('editProduct.updateIdle')
                : batch.isBatchMode
                  ? batch.isLastDraft
                    ? t('editProduct.batch.createAllIdle')
                    : t('editProduct.batch.nextProduct')
                  : t('editProduct.createIdle')
            }
            disabled={batch.isBatchMode ? pendingDraftCount === 0 : !modelUrl}
            loading={batch.isBatchMode ? batch.isSubmitting : isSubmitting}
            hasPendingChanges={batch.isBatchMode ? pendingDraftCount > 0 : hasPendingChanges}
          />
        )}

        {successPopupData && !batch.isBatchMode && (
          <SuccessPopup
            opened={!!successPopupData}
            onClose={() => setSuccessPopupData(null)}
            productId={successPopupData.productId}
            productName={successPopupData.productName}
            onResetForm={handleResetForm}
          />
        )}

        <ProductBatchSuccessModal
          result={batch.result}
          onDismiss={batch.dismissResult}
          onGoToDashboard={() => {
            batch.reset();
            handleResetForm();
          }}
          onCreateMore={() => {
            batch.reset();
            handleResetForm();
          }}
        />
      </Center>
    </SessionAuth>
  );
}
